import React, { useState, useEffect, useMemo } from 'react';
import { usersAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Plus, Edit, Trash2, CheckCircle, XCircle, Search, X, Upload } from 'lucide-react';
import { toast } from 'sonner';
import CreateUserDialog from '../components/CreateUserDialog';
import EditUserDialog from '../components/EditUserDialog';
import ImportUsersDialog from '../components/ImportUsersDialog';
import { usePermissions } from '../hooks/usePermissions';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { isAdmin, canCreate, canEdit } = usePermissions();

  // Filtrar y ordenar usuarios
  const usuariosFiltrados = useMemo(() => {
    return usuarios
      .filter(user => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase();
        return (
          user.nombre?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term) ||
          user.rol_nombre?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
  }, [usuarios, searchTerm]);

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsuarios(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    if (!canEdit('/usuarios') && !isAdmin()) {
      toast.error('No tiene permisos para editar usuarios');
      return;
    }
    // Buscar la referencia más reciente del user en el estado para evitar estado stale
    const fresh = usuarios.find(u => u.id === user.id) || user;
    setSelectedUser(fresh);
    setShowEdit(true);
  };

  const handleToggleStatus = async (user) => {
    if (!canEdit('/usuarios') && !isAdmin()) {
      toast.error('No tiene permisos para cambiar el estado');
      return;
    }

    try {
      await usersAPI.update(user.id, { activo: !user.activo });
      toast.success(`Usuario ${!user.activo ? 'activado' : 'desactivado'}`);
      loadUsuarios();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const getRoleBadge = (rol_nombre) => {
    const colors = {
      'Administrador': 'bg-purple-100 text-purple-700',
      'Jefe Postventa': 'bg-blue-100 text-blue-700',
      'Supervisor': 'bg-green-100 text-green-700',
      'Cliente': 'bg-gray-100 text-gray-700',
    };
    return colors[rol_nombre] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9d8272]"></div>
      </div>
    );
  }

  const canCreateUser = canCreate('/usuarios') || isAdmin();
  const canEditUser = canEdit('/usuarios') || isAdmin();

  return (
    <div className="skin-page space-y-4 lg:space-y-6" data-testid="usuarios-page">
      {/* Header */}
      <div className="skin-page-head flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 data-testid="usuarios-title">Usuarios</h1>
          <p className="hidden sm:block">Gestiona usuarios y sus roles</p>
        </div>
        {canCreateUser && (
          <div className="flex gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => setShowImport(true)}
              className="skin-btn"
              data-testid="import-users-button"
            >
              <Upload size={14} />
              <span className="sm:inline">Importar</span>
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="skin-btn skin-btn-primary"
              data-testid="create-user-button"
            >
              <Plus size={14} />
              <span className="sm:inline">Nuevo Usuario</span>
            </button>
          </div>
        )}
      </div>

      {/* Filtro de búsqueda */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 skin-text-mute" />
          <input
            type="text"
            placeholder="Buscar por nombre, correo o rol..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="skin-input pl-9 pr-9"
            data-testid="search-users-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 skin-text-mute hover:skin-text-ink transition-colors"
              data-testid="clear-search-btn"
            >
              <X size={16} />
            </button>
          )}
        </div>
        {searchTerm && (
          <span className="text-sm skin-text-mute">
            Mostrando {usuariosFiltrados.length} de {usuarios.length} usuarios
          </span>
        )}
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {usuariosFiltrados.map((user) => (
          <Card
            key={user.id}
            className="skin-panel hover:shadow-md transition-shadow"
            data-testid="user-card"
          >
            <CardHeader className="p-3 sm:p-4 lg:p-6 pb-2 sm:pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base sm:text-lg font-semibold skin-text-ink mb-1 sm:mb-2 truncate">
                    {user.nombre}
                  </CardTitle>
                  <p className="text-xs sm:text-sm skin-text-mute mb-2 truncate">{user.email}</p>
                  <Badge className={`${getRoleBadge(user.rol_nombre)} border-0 text-xs`}>
                    {user.rol_nombre || 'Sin rol'}
                  </Badge>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  {user.activo ? (
                    <CheckCircle size={18} className="sm:w-5 sm:h-5" style={{ color: 'var(--skin-green)' }} />
                  ) : (
                    <XCircle size={18} className="sm:w-5 sm:h-5" style={{ color: 'var(--skin-badge-danger-text)' }} />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 lg:p-6 pt-0">
              {canEditUser && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(user)}
                    className="skin-btn flex-1 justify-center"
                    data-testid="edit-user-button"
                  >
                    <Edit size={14} />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleStatus(user)}
                    className="skin-btn flex-1 justify-center"
                    style={user.activo
                      ? { color: 'var(--skin-badge-danger-text)', borderColor: 'var(--skin-badge-danger-text)' }
                      : { color: 'var(--skin-green)', borderColor: 'var(--skin-green)' }}
                    data-testid="toggle-status-button"
                  >
                    {user.activo ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {usuariosFiltrados.length === 0 && (
        <div className="skin-panel">
          <div className="text-center py-12">
            {searchTerm ? (
              <div>
                <p className="skin-text-mute mb-2">No se encontraron usuarios con "{searchTerm}"</p>
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="skin-btn"
                >
                  Limpiar búsqueda
                </button>
              </div>
            ) : (
              <p className="skin-text-mute">No hay usuarios registrados</p>
            )}
          </div>
        </div>
      )}

      {/* Dialogs */}
      {showCreate && (
        <CreateUserDialog
          open={showCreate}
          onClose={() => {
            setShowCreate(false);
            loadUsuarios();
          }}
        />
      )}

      {showEdit && selectedUser && (
        <EditUserDialog
          user={selectedUser}
          open={showEdit}
          onClose={(updatedUser) => {
            setShowEdit(false);
            setSelectedUser(null);
            // Actualización optimista inmediata para evitar estado stale al reabrir
            if (updatedUser && updatedUser.id) {
              setUsuarios(prev => prev.map(u => u.id === updatedUser.id ? { ...u, ...updatedUser } : u));
            }
            loadUsuarios();
          }}
        />
      )}
      {showImport && (
        <ImportUsersDialog
          open={showImport}
          onClose={(success) => {
            setShowImport(false);
            if (success) loadUsuarios();
          }}
        />
      )}
    </div>
  );
};

export default Usuarios;
